<template>
	<div>
		<header class="ezcache-screen-header">
			<svg viewBox="0 0 24 24">
				<path d="M12,16A3,3 0 0,1 9,13C9,11.88 9.61,10.9 10.5,10.39L20.21,4.77L14.68,14.35C14.18,15.33 13.17,16 12,16M12,3C13.81,3 15.5,3.5 16.97,4.32L14.87,5.53C14,5.19 13,5 12,5A8,8 0 0,0 4,13C4,15.21 4.89,17.21 6.34,18.65H6.35C6.74,19.04 6.74,19.67 6.35,20.06C5.96,20.45 5.32,20.45 4.93,20.07V20.07C3.12,18.26 2,15.76 2,13A10,10 0 0,1 12,3M22,13C22,15.76 20.88,18.26 19.07,20.07V20.07C18.68,20.45 18.05,20.45 17.66,20.06C17.27,19.67 17.27,19.04 17.66,18.65V18.65C19.11,17.2 20,15.21 20,13C20,12 19.81,11 19.46,10.1L20.67,8C21.5,9.5 22,11.18 22,13Z" />
			</svg>
			{{ $wp.trans('performance') }}
		</header>

		<Loader v-if="loading" />
		<div v-else>
			<div class="notice notice-success" v-if="flashMessage">
				<p>{{ flashMessage }}</p>
			</div>

			<!-- Cache Preload Status -->
			<div class="ezcache-card mb-4">
				<h2>{{ $wp.trans('cache_preload') }}</h2>
			<p class="mb-3">
				<strong>{{ $wp.trans('status') }}:</strong> {{ preloadStatus.status }}
				&middot;
				{{ $wp.trans('processed') }}: {{ preloadStatus.processed }} / {{ preloadStatus.total }} ({{ preloadStatus.remaining }} {{ $wp.trans('remaining') }})
			</p>
			<div class="d-flex">
				<button type="button" class="wpb-button wpb-button-sm wpb-button-primary me-2" @click="runPreload" v-text="$wp.trans('run_preload_now')"></button>
				<button type="button" class="wpb-button wpb-button-sm wpb-button-outlined wpb-button-primary" @click="stopPreload" v-text="$wp.trans('stop_preload')"></button>
			</div>
		</div>

		<!-- Settings Form -->
		<form @submit.prevent="saveSettings">
			<!-- Preload Settings -->
			<div class="ezcache-card mb-4">
				<h2>{{ $wp.trans('preload_settings') }}</h2>
				<table class="ezcache-form-table">
					<tr>
						<th><label for="enable_preload">{{ $wp.trans('enable_preload') }}</label></th>
						<td>
							<input type="checkbox" id="enable_preload" v-model="settings.enable_preload">
							<p class="description">{{ $wp.trans('enable_preload_desc') }}</p>
						</td>
					</tr>
					<tr>
						<th><label for="preload_on_cache_clear">{{ $wp.trans('preload_after_clear') }}</label></th>
						<td><input type="checkbox" id="preload_on_cache_clear" v-model="settings.preload_on_cache_clear"></td>
					</tr>
					<tr>
						<th><label for="preload_crawl_homepage_links">{{ $wp.trans('crawl_homepage') }}</label></th>
						<td><input type="checkbox" id="preload_crawl_homepage_links" v-model="settings.preload_crawl_homepage_links"></td>
					</tr>
					<tr>
						<th><label for="preload_sitemap_url">{{ $wp.trans('sitemap_url') }}</label></th>
						<td>
							<input type="text" id="preload_sitemap_url" v-model="settings.preload_sitemap_url" class="regular-text" :placeholder="$wp.site_url + '/sitemap.xml'">
							<p class="description">{{ $wp.trans('sitemap_url_desc') }}</p>
						</td>
					</tr>
					<tr>
						<th><label for="preload_batch_size">{{ $wp.trans('urls_per_batch') }}</label></th>
						<td><input type="number" id="preload_batch_size" v-model.number="settings.preload_batch_size" min="1" max="50"></td>
					</tr>
				</table>
			</div>

			<!-- Front-end Optimizations -->
			<div class="ezcache-card mb-4">
				<h2>{{ $wp.trans('frontend_optimizations') }}</h2>
				<table class="ezcache-form-table">
					<tr>
						<th><label for="lazy_load_images">{{ $wp.trans('lazy_load_images') }}</label></th>
						<td><input type="checkbox" id="lazy_load_images" v-model="settings.lazy_load_images"></td>
					</tr>
					<tr>
						<th><label for="lazy_load_iframes">{{ $wp.trans('lazy_load_iframes') }}</label></th>
						<td><input type="checkbox" id="lazy_load_iframes" v-model="settings.lazy_load_iframes"></td>
					</tr>
					<tr>
						<th><label for="defer_js">{{ $wp.trans('defer_js') }}</label></th>
						<td>
							<input type="checkbox" id="defer_js" v-model="settings.defer_js">
							<p class="description">{{ $wp.trans('defer_js_desc') }}</p>
						</td>
					</tr>
					<tr>
						<th><label for="defer_js_exclusions">{{ $wp.trans('defer_js_exclusions') }}</label></th>
						<td>
							<textarea id="defer_js_exclusions" v-model="settings.defer_js_exclusions" rows="4" class="large-text code" placeholder="jquery.js&#10;jquery-migrate"></textarea>
							<p class="description">{{ $wp.trans('defer_js_exclusions_desc') }}</p>
						</td>
					</tr>
					<tr>
						<th><label for="remove_query_strings">{{ $wp.trans('remove_query_strings') }}</label></th>
						<td><input type="checkbox" id="remove_query_strings" v-model="settings.remove_query_strings"></td>
					</tr>
					<tr>
						<th><label for="dns_prefetch">{{ $wp.trans('dns_prefetch') }}</label></th>
						<td><textarea id="dns_prefetch" v-model="settings.dns_prefetch" rows="4" class="large-text code" placeholder="//fonts.googleapis.com&#10;//cdn.example.com"></textarea></td>
					</tr>
					<tr>
						<th><label for="preconnect">{{ $wp.trans('preconnect') }}</label></th>
						<td><textarea id="preconnect" v-model="settings.preconnect" rows="4" class="large-text code" placeholder="https://fonts.gstatic.com"></textarea></td>
					</tr>
				</table>
			</div>

			<!-- Heartbeat Control -->
			<div class="ezcache-card mb-4">
				<h2>{{ $wp.trans('heartbeat_control') }}</h2>
				<table class="ezcache-form-table">
					<tr>
						<th><label for="heartbeat_control">{{ $wp.trans('enable_heartbeat') }}</label></th>
						<td><input type="checkbox" id="heartbeat_control" v-model="settings.heartbeat_control"></td>
					</tr>
					<tr>
						<th><label for="heartbeat_mode">{{ $wp.trans('mode') }}</label></th>
						<td>
							<select id="heartbeat_mode" v-model="settings.heartbeat_mode">
								<option value="reduce">{{ $wp.trans('reduce_activity') }}</option>
								<option value="disable">{{ $wp.trans('disable_completely') }}</option>
							</select>
						</td>
					</tr>
				</table>
			</div>

			<!-- CDN -->
			<div class="ezcache-card mb-4">
				<h2>{{ $wp.trans('cdn') }}</h2>
				<table class="ezcache-form-table">
					<tr>
						<th><label for="cdn_enabled">{{ $wp.trans('enable_cdn') }}</label></th>
						<td><input type="checkbox" id="cdn_enabled" v-model="settings.cdn_enabled"></td>
					</tr>
					<tr>
						<th><label for="cdn_url">{{ $wp.trans('cdn_url') }}</label></th>
						<td>
							<input type="text" id="cdn_url" v-model="settings.cdn_url" class="regular-text" placeholder="https://cdn.example.com">
							<p class="description">{{ $wp.trans('cdn_url_desc') }}</p>
						</td>
					</tr>
				</table>
			</div>

			<!-- Database Cleanup -->
			<div class="ezcache-card mb-4">
				<h2>{{ $wp.trans('database_cleanup') }}</h2>
				<table class="ezcache-form-table">
					<tr>
						<th><label for="db_cleanup_revisions">{{ $wp.trans('delete_revisions') }}</label></th>
						<td><input type="checkbox" id="db_cleanup_revisions" v-model="settings.db_cleanup_revisions"></td>
					</tr>
					<tr>
						<th><label for="db_cleanup_auto_drafts">{{ $wp.trans('delete_auto_drafts') }}</label></th>
						<td><input type="checkbox" id="db_cleanup_auto_drafts" v-model="settings.db_cleanup_auto_drafts"></td>
					</tr>
					<tr>
						<th><label for="db_cleanup_trashed_posts">{{ $wp.trans('delete_trashed_posts') }}</label></th>
						<td><input type="checkbox" id="db_cleanup_trashed_posts" v-model="settings.db_cleanup_trashed_posts"></td>
					</tr>
					<tr>
						<th><label for="db_cleanup_spam_comments">{{ $wp.trans('delete_spam_comments') }}</label></th>
						<td><input type="checkbox" id="db_cleanup_spam_comments" v-model="settings.db_cleanup_spam_comments"></td>
					</tr>
					<tr>
						<th><label for="db_cleanup_trashed_comments">{{ $wp.trans('delete_trashed_comments') }}</label></th>
						<td><input type="checkbox" id="db_cleanup_trashed_comments" v-model="settings.db_cleanup_trashed_comments"></td>
					</tr>
					<tr>
						<th><label for="db_cleanup_expired_transients">{{ $wp.trans('delete_transients') }}</label></th>
						<td><input type="checkbox" id="db_cleanup_expired_transients" v-model="settings.db_cleanup_expired_transients"></td>
					</tr>
					<tr>
						<th><label for="db_cleanup_orphan_postmeta">{{ $wp.trans('delete_orphan_meta') }}</label></th>
						<td><input type="checkbox" id="db_cleanup_orphan_postmeta" v-model="settings.db_cleanup_orphan_postmeta"></td>
					</tr>
					<tr>
						<th><label for="db_optimize_tables">{{ $wp.trans('optimize_tables') }}</label></th>
						<td><input type="checkbox" id="db_optimize_tables" v-model="settings.db_optimize_tables"></td>
					</tr>
					<tr>
						<th><label for="db_cleanup_schedule">{{ $wp.trans('cleanup_schedule') }}</label></th>
						<td>
							<select id="db_cleanup_schedule" v-model="settings.db_cleanup_schedule">
								<option value="never">{{ $wp.trans('never') }}</option>
								<option value="daily">{{ $wp.trans('daily') }}</option>
								<option value="weekly">{{ $wp.trans('weekly') }}</option>
							</select>
						</td>
					</tr>
				</table>
				<div class="mt-3">
					<button type="button" class="wpb-button wpb-button-sm wpb-button-outlined wpb-button-primary" @click="runDbCleanup" v-text="$wp.trans('run_cleanup_now')"></button>
				</div>
			</div>

			<!-- Save Button -->
			<div class="ezcache-card">
				<button type="submit" class="wpb-button wpb-button-lg wpb-button-primary" :disabled="saving">
					<span v-if="saving" v-text="$wp.trans('saving') + '...'"></span>
					<span v-else v-text="$wp.trans('save_settings')"></span>
				</button>
			</div>
		</form>
		</div>
	</div>
</template>

<script>
import Loader from "../Components/Loader";
import API from "../Utilities/Api";

export default {
	components: { Loader },
	data() {
		return {
			loading: true,
			saving: false,
			flashMessage: '',
			preloadStatus: {
				status: 'idle',
				processed: 0,
				total: 0,
				remaining: 0
			},
			settings: {
				enable_preload: false,
				preload_on_cache_clear: false,
				preload_crawl_homepage_links: false,
				preload_sitemap_url: '',
				preload_batch_size: 10,
				lazy_load_images: false,
				lazy_load_iframes: false,
				defer_js: false,
				defer_js_exclusions: '',
				remove_query_strings: false,
				dns_prefetch: '',
				preconnect: '',
				heartbeat_control: false,
				heartbeat_mode: 'reduce',
				cdn_enabled: false,
				cdn_url: '',
				db_cleanup_revisions: false,
				db_cleanup_auto_drafts: false,
				db_cleanup_trashed_posts: false,
				db_cleanup_spam_comments: false,
				db_cleanup_trashed_comments: false,
				db_cleanup_expired_transients: false,
				db_cleanup_orphan_postmeta: false,
				db_optimize_tables: false,
				db_cleanup_schedule: 'never'
			}
		};
	},
	mounted() {
		this.loadSettings();
	},
	methods: {
		async loadSettings() {
			try {
				const response = await API.get('performance');
				if (response.data.success) {
					this.settings = { ...this.settings, ...response.data.data.settings };
					this.preloadStatus = response.data.data.preload_status || this.preloadStatus;
				}
			} catch (e) {
				console.error('Failed to load settings', e);
			} finally {
				this.loading = false;
			}
		},
		async saveSettings() {
			this.saving = true;
			try {
				await API.post('performance', this.settings);
				this.toast(this.$wp.trans('settings_saved'), 'success');
			} catch (e) {
				this.toast(this.$wp.trans('error_saving_settings'), 'error');
			} finally {
				this.saving = false;
			}
		},
		async runPreload() {
			try {
				await API.post('performance/preload');
				this.flashMessage = this.$wp.trans('preload_started');
				setTimeout(() => this.flashMessage = '', 3000);
				this.loadSettings();
			} catch (e) {
				this.toast(this.$wp.trans('error'), 'error');
			}
		},
		async stopPreload() {
			try {
				await API.delete('performance/preload');
				this.flashMessage = this.$wp.trans('preload_stopped');
				setTimeout(() => this.flashMessage = '', 3000);
				this.loadSettings();
			} catch (e) {
				this.toast(this.$wp.trans('error'), 'error');
			}
		},
		async runDbCleanup() {
			try {
				await API.post('performance/db-cleanup');
				this.toast(this.$wp.trans('db_cleaned'), 'success');
			} catch (e) {
				this.toast(this.$wp.trans('error'), 'error');
			}
		},
		toast(message, type) {
			this.$swal({
				toast: true,
				position: 'top-end',
				icon: type,
				title: message,
				showConfirmButton: false,
				timer: 3000
			});
		}
	}
};
</script>

<style scoped>
.ezcache-card {
	background: #fff;
	border: 1px solid #c3c4c7;
	border-radius: 4px;
	padding: 20px;
	box-shadow: 0 1px 1px rgba(0,0,0,.04);
}

.ezcache-form-table {
	width: 100%;
	border-collapse: collapse;
}

.ezcache-form-table th {
	text-align: left;
	padding: 15px 10px 15px 0;
	width: 200px;
	vertical-align: top;
	font-weight: 600;
}

.ezcache-form-table td {
	padding: 15px 0;
}

.ezcache-form-table tr {
	border-bottom: 1px solid #f0f0f1;
}

.ezcache-form-table tr:last-child {
	border-bottom: none;
}

.description {
	color: #646970;
	font-size: 13px;
	margin-top: 4px;
}

.regular-text {
	width: 350px;
	max-width: 100%;
}

.large-text {
	width: 100%;
	max-width: 500px;
}

.button-group {
	display: flex;
	gap: 10px;
}

.mb-3 { margin-bottom: 15px; }
.mb-4 { margin-bottom: 20px; }
.mt-3 { margin-top: 15px; }
.ms-2 { margin-left: 10px; }

h2 {
	margin: 0 0 15px 0;
	padding: 0;
	font-size: 1.3em;
	border-bottom: 1px solid #f0f0f1;
	padding-bottom: 10px;
}
</style>
